import {
  addElement,
  defaultGenericElement,
  updateElement,
} from '../../lib/element';
import { history } from '../../lib/history';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';
import { distance } from '../../utils/math';
import { drawGrid } from '../grid/utils/gridLineUtils';
import { poly, type GenericPolyElement } from '../poly';
import { getPolyElementAbsoluteCoords } from '../poly/utils/coords';
import { getPathFromPoints } from '../poly/utils/path';
import { getPolyShape } from '../poly/utils/shape';

export type GridPlugin = Plugin<
  GenericPolyElement,
  typeof gridActions,
  typeof gridState
>;

export interface GridElement extends GenericPolyElement {
  type: 'grid';
}

const gridState = {
  newStrokeColor: '#3c87f7',
  newStrokeWidth: 4,
};

const gridActions = {
  createOrShow: (existingGridId?: string) => {
    if (existingGridId) {
      const existingGrid = useEditorStore
        .getState()
        .elements.get(existingGridId);

      if (existingGrid == null) return;

      const elements = useEditorStore.getState().elements;

      updateElement(elements, existingGrid, {
        isVisible: !existingGrid.isVisible,
      });
      renderStaticScene();

      return;
    }

    const newElement: GenericPolyElement = {
      ...defaultGenericElement(),
      originX: 900,
      originY: 500,
      points: [
        [0, 100],
        [200, 0],
        [400, 100],
        [400, 500],
        [200, 400],
        [0, 500],
      ],
      path: new Path2D(),
      strokeColor: gridState.newStrokeColor,
      strokeWidth: gridState.newStrokeWidth,
      type: 'edgeGrid',
      x: 900,
      y: 500,
    };

    const { x1, y1, x2, y2 } = getPolyElementAbsoluteCoords(newElement);

    newElement.width = distance(x1, x2) + gridState.newStrokeWidth;
    newElement.height = distance(y1, y2) + gridState.newStrokeWidth;
    newElement.shape = getPolyShape(newElement);

    addElement(useEditorStore.getState().elements, newElement);
    history.push(updateEditorStore({ selectedElementIds: [newElement.id] }));

    updateRuntimeStore({
      activeTool: 'selection',
    });

    renderStaticScene();
  },
  setStrokeColor: (color: string) => {
    gridState.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    gridState.newStrokeWidth = width;
  },
};

const plugin: GridPlugin = {
  name: 'edgeGrid',
  cursor: 'default',
  actions: gridActions,
  state: gridState,
  events: {},
  render: {
    padding: (element) => element.strokeWidth,
    draw: ({
      config,
      context,
      element,
      runtimeState,
      elementState,
      skipElementUpdate,
    }) => {
      if (
        (!runtimeState.newElement && !element.points.length) ||
        !element.isVisible
      )
        return;

      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = element.strokeWidth / 2;
      context.strokeStyle = element.strokeColor;

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const path = getPathFromPoints(
        element.points,
        element.bezierHandles,
        true,
      );
      context.stroke(path);

      if (!skipElementUpdate) {
        updateElement<GenericPolyElement>(elementState.elements, element, {
          path,
        });
      }

      context.lineWidth = 1;

      const lineCount = 15;

      const pathLeft = drawGrid(
        [
          element.points[0],
          element.points[1],
          element.points[4],
          element.points[5],
        ],
        lineCount,
      );

      context.stroke(pathLeft);

      const pathRight = drawGrid(
        [
          element.points[1],
          element.points[2],
          element.points[3],
          element.points[4],
        ],
        lineCount,
      );

      context.stroke(pathRight);

      context.stroke();

      context.restore();
    },
    translate: poly.render.translate,
  },
  collision: poly.collision,
  transformation: poly.transformation,
  selection: {
    ...poly.selection,
    renderSelectionBorder: () => undefined,
    hideDefaultTransformHandles: true,
  },
  export: poly.export,
  import: poly.import,
};

export const edgeGrid = plugin;
